import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { Pill } from '../../layout/Pill.jsx';
import { formatIQD, formatDate, formatDateTime } from '../../shared/format.js';
import { useAuth } from '../../shared/useAuth.js';
import { useShiftStatus, useShiftUI } from '../../shared/useShift.js';
import { toast } from '../../shared/useToast.js';
import { PrintLogo } from '../../shared/PrintLogo.jsx';
import { ExportButton } from '../reports/components/ExportButton.jsx';
import { ConfirmModal } from '../pricing/components/ConfirmModal.jsx';
import {
  useSupplierAccounts,
  useSupplierAccount,
  useSupplierPayments,
  useReverseVoucher,
  useMigrateLegacy
} from './hooks/useSupplierAccounts.js';
import { useSuppliers } from '../purchases/hooks/useSuppliers.js';
import { SettleSupplierModal, PaymentStatusPill, METHOD_LABELS } from './components/SettleSupplierModal.jsx';
import { StatementModal } from './components/StatementModal.jsx';
import { SupplierReports } from './components/SupplierReports.jsx';
import { VoucherPrint } from './components/VoucherPrint.jsx';
import { Modal } from './components/Modal.jsx';
import './components/print.css';

const STATES = [
  { key: 'all', label: 'جميع الموردين' },
  { key: 'due', label: 'لديهم مستحقات' },
  { key: 'settled', label: 'مسددون بالكامل' },
  { key: 'partial', label: 'لديهم فواتير مسددة جزئياً' },
  { key: 'credit', label: 'لديهم رصيد دائن' },
  { key: 'review', label: 'بحاجة مراجعة' }
];

const TABS = [
  { key: 'accounts', label: 'حسابات الموردين' },
  { key: 'vouchers', label: 'سندات الصرف' },
  { key: 'reports', label: 'تقارير' }
];

function StateBadge({ state, label }) {
  const variant = state === 'settled' ? 'success'
    : state === 'partial' ? 'warning'
      : state === 'credit' ? 'info' : 'danger';
  return <Pill variant={variant}>{label}</Pill>;
}

function StatCards({ stats }) {
  const cards = [
    { label: 'عدد الموردين', value: stats ? stats.suppliers : 0, color: '#4B5563' },
    { label: 'عليهم مستحقات', value: stats ? stats.suppliersWithDebt : 0, color: '#B91C1C' },
    { label: 'إجمالي المشتريات', value: formatIQD(stats ? stats.purchasesTotal : 0), color: '#1D4ED8' },
    { label: 'إجمالي المدفوع', value: formatIQD(stats ? stats.paidTotal : 0), color: '#15803D' },
    {
      label: 'إجمالي المستحق للموردين',
      value: formatIQD(stats ? stats.remainingTotal : 0),
      color: stats && stats.remainingTotal > 0 ? '#B91C1C' : '#15803D'
    },
    { label: 'رصيد دائن لنا', value: formatIQD(stats ? stats.creditBalance : 0), color: '#0E7490' }
  ];
  return (
    <div className="grid grid-cols-6 gap-3">
      {cards.map((k) => (
        <div key={k.label} className="card p-3.5 kpi-card">
          <div className="text-[11.5px] text-ink-3">{k.label}</div>
          <div className="text-[18px] font-extrabold mt-1" style={{ color: k.color }}>{k.value}</div>
        </div>
      ))}
    </div>
  );
}

/** نافذة تفاصيل حساب مورد: فواتيره وسنداته، مع «تسديد» لكل فاتورة. */
function SupplierDetailModal({ supplierId, onClose, onSettleInvoice, onSettleAccount, onStatement, canSettle, onVoucher }) {
  const q = useSupplierAccount(supplierId);
  const [tab, setTab] = useState('invoices');
  const data = q.data;
  const summary = data?.summary;
  const invoices = data?.invoices || [];
  const payments = data?.payments || [];

  return (
    <Modal
      title={data?.supplier?.name || 'حساب المورد'}
      sub={data?.supplier?.phone ? `هاتف: ${data.supplier.phone}` : 'فواتير المورد وسندات صرفه'}
      width={1040}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إغلاق</button>
          <button type="button" className="btn flex-1 justify-center" onClick={onStatement}>
            <Icon name="reports" size={14} /> كشف الحساب
          </button>
          {canSettle && Number(summary?.remainingTotal || 0) > 0 ? (
            <button type="button" className="btn btn-primary flex-1 justify-center" onClick={onSettleAccount}>
              <Icon name="check" size={14} /> تسديد على الحساب
            </button>
          ) : null}
        </>
      )}
    >
      {q.isLoading ? (
        <div className="text-center py-8 text-ink-3">جاري التحميل...</div>
      ) : (
        <>
          <div className="grid grid-cols-5 gap-2">
            {[
              { label: 'إجمالي المشتريات', value: formatIQD(summary?.purchasesTotal || 0), color: '#1D4ED8' },
              { label: 'إجمالي المدفوع', value: formatIQD(summary?.paidTotal || 0), color: '#15803D' },
              { label: 'إجمالي المتبقي', value: formatIQD(summary?.remainingTotal || 0), color: '#B91C1C' },
              { label: 'رصيد دائن', value: formatIQD(summary?.creditBalance || 0), color: '#0E7490' },
              { label: 'فواتير مستحقة', value: summary?.openInvoices || 0, color: '#4B5563' }
            ].map((k) => (
              <div key={k.label} className="card p-2.5">
                <div className="text-[11px] text-ink-3">{k.label}</div>
                <div className="text-[15px] font-extrabold mt-0.5" style={{ color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
            {[
              { key: 'invoices', label: `الفواتير (${invoices.length})` },
              { key: 'payments', label: `سندات الصرف (${payments.length})` }
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={clsx(
                  'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold',
                  tab === t.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'invoices' ? (
            !invoices.length ? (
              <div className="text-center py-8 text-ink-3">لا فواتير لهذا المورد</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="text-ink-3 border-b border-line text-right">
                      <th className="p-2">الفاتورة</th>
                      <th className="p-2">التاريخ</th>
                      <th className="p-2">الاستحقاق</th>
                      <th className="p-2">الإجمالي</th>
                      <th className="p-2">المدفوع</th>
                      <th className="p-2">المتبقي</th>
                      <th className="p-2">الحالة</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((i) => (
                      <tr key={String(i._id)} className="border-b border-line">
                        <td className="p-2 font-mono">
                          {i.number}
                          {i.needsReview ? (
                            <span title={i.reviewReason} className="ms-1"><Pill variant="warning">مراجعة</Pill></span>
                          ) : null}
                        </td>
                        <td className="p-2 text-ink-3">{formatDate(i.date)}</td>
                        <td className="p-2 text-ink-3">{i.dueDate ? formatDate(i.dueDate) : '—'}</td>
                        <td className="p-2">{formatIQD(i.total)}</td>
                        <td className="p-2 text-green font-semibold">{formatIQD(i.paid)}</td>
                        <td className="p-2 font-extrabold" style={{ color: i.due ? '#B91C1C' : '#15803D' }}>
                          {formatIQD(i.due)}
                        </td>
                        <td className="p-2"><PaymentStatusPill status={i.paymentStatus} /></td>
                        <td className="p-2">
                          {canSettle && i.status === 'posted' && Number(i.due) > 0 ? (
                            <button type="button" className="btn btn-primary" onClick={() => onSettleInvoice(i)}>
                              تسديد
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : !payments.length ? (
            <div className="text-center py-8 text-ink-3">لا سندات صرف لهذا المورد</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-ink-3 border-b border-line text-right">
                    <th className="p-2">رقم السند</th>
                    <th className="p-2">التاريخ</th>
                    <th className="p-2">النوع</th>
                    <th className="p-2">المبلغ</th>
                    <th className="p-2">الطريقة</th>
                    <th className="p-2">القاصة</th>
                    <th className="p-2">المستخدم</th>
                    <th className="p-2">الحالة</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={String(p._id)} className="border-b border-line">
                      <td className="p-2 font-mono">{p.voucherNumber}</td>
                      <td className="p-2 text-ink-3">{formatDateTime(p.paidAt)}</td>
                      <td className="p-2">{p.direction === 'out' ? 'تسديد' : 'استرداد'}</td>
                      <td className={clsx('p-2 font-bold', p.status === 'reversed' && 'line-through text-ink-3')}>
                        {formatIQD(p.amount)}
                      </td>
                      <td className="p-2">{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</td>
                      <td className="p-2 text-ink-3">{p.cashboxName || '—'}</td>
                      <td className="p-2 text-ink-3">{p.userName || '—'}</td>
                      <td className="p-2">
                        {p.status === 'reversed'
                          ? <Pill variant="danger">معكوس</Pill>
                          : <Pill variant="success">مرحّل</Pill>}
                      </td>
                      <td className="p-2">
                        <button type="button" className="btn" style={{ padding: '4px 8px' }} onClick={() => onVoucher(p)}>
                          <Icon name="print" size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export function SuppliersScreen() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canView = hasPermission('suppliers.accounts.view') || hasPermission('inventory.purchase.view')
    || hasPermission('inventory.purchase.manage');
  const canSettle = hasPermission('suppliers.settle') || hasPermission('inventory.purchase.manage');
  const canReverse = canSettle && hasPermission('inventory.purchase.post');
  const canMigrate = hasPermission('inventory.purchase.manage');
  const { shift, blocked } = useShiftStatus();
  const openShiftModal = useShiftUI((s) => s.openModal);

  const [tab, setTab] = useState('accounts');
  const [state, setState] = useState('all');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  // «عرض الكل» حالة مستقلة تماماً عن الفلاتر: تغيير أي فلتر لا يعيدنا
  // للترقيم بصمت — وهذا مطلب صريح في مستند العميل.
  const [showAll, setShowAll] = useState(false);

  const [detailId, setDetailId] = useState(null);
  const [statementFor, setStatementFor] = useState(null);
  const [settleTarget, setSettleTarget] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const tableRef = useRef(null);

  const reverse = useReverseVoucher();
  const migrate = useMigrateLegacy();

  const params = useMemo(() => {
    const p = { state };
    if (search.trim()) p.q = search.trim();
    if (from) p.from = from;
    if (to) p.to = to;
    if (showAll) p.all = true;
    else { p.page = page; p.limit = 20; }
    return p;
  }, [state, search, from, to, showAll, page]);

  const listQ = useSupplierAccounts(params);
  const rows = listQ.data?.items || [];
  const stats = listQ.data?.stats;
  const mode = listQ.data?.mode || 'page';
  const notice = listQ.data?.notice;

  const voucherParams = useMemo(() => {
    const p = { all: true };
    if (search.trim()) p.q = search.trim();
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [search, from, to]);
  const vouchersQ = useSupplierPayments(voucherParams, tab === 'vouchers');
  const vouchers = vouchersQ.data?.items || [];

  // قائمة كل الموردين لمُنسدلة التقارير — مستقلة عن فلاتر الجدول أعلاه.
  const allSuppliersQ = useSuppliers({ active: undefined, limit: 200 });
  const allSuppliers = allSuppliersQ.data?.items || [];

  function setFilter(fn) {
    // الفلاتر تُعيد الصفحة للأولى، لكنها لا تُطفئ «عرض الكل».
    fn();
    setPage(1);
  }

  const exportRows = rows.map((r) => ({
    name: r.name,
    phone: r.phone,
    purchasesTotal: r.purchasesTotal,
    paidTotal: r.paidTotal,
    remainingTotal: r.remainingTotal,
    openInvoices: r.openInvoices,
    accountStateLabel: r.accountStateLabel
  }));
  const exportColumns = [
    { key: 'name', label: 'اسم المورد' },
    { key: 'phone', label: 'الهاتف' },
    { key: 'purchasesTotal', label: 'إجمالي المشتريات' },
    { key: 'paidTotal', label: 'إجمالي المدفوع' },
    { key: 'remainingTotal', label: 'إجمالي المتبقي' },
    { key: 'openInvoices', label: 'عدد الفواتير المستحقة' },
    { key: 'accountStateLabel', label: 'حالة الحساب' }
  ];

  async function doReverse() {
    if (!reverseTarget) return;
    if (reverseReason.trim().length < 3) {
      toast.error('سبب العكس مطلوب (3 أحرف على الأقل)');
      return;
    }
    await reverse.mutateAsync(
      { paymentId: String(reverseTarget._id), reason: reverseReason.trim() },
      { onSuccess: () => { setReverseTarget(null); setReverseReason(''); } }
    );
  }

  async function doMigrate() {
    const res = await migrate.mutateAsync({});
    setMigrateResult(res);
    setMigrateOpen(false);
    toast.success(
      `تمت معالجة ${res.invoicesScanned} فاتورة — حُدِّث ${res.invoicesUpdated} سجلاً `
      + `وعُلِّم ${res.flaggedCount} للمراجعة، بلا أي حركة قاصة جديدة.`
    );
  }

  if (!canView) {
    return <Card><div className="text-center py-10 text-ink-3">لا تملك صلاحية عرض حسابات الموردين</div></Card>;
  }

  return (
    <div className="space-y-3.5">
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0">حسابات الموردين</h2>
          <div className="text-ink-3 text-xs mt-0.5">
            المستحقات والتسديدات وكشوف الحسابات — كل الأرقام مشتقّة من فواتير المشتريات
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canMigrate ? (
            <button type="button" className="btn" onClick={() => setMigrateOpen(true)}>
              <Icon name="clock" size={14} /> معالجة البيانات السابقة
            </button>
          ) : null}
        </div>
      </div>

      {blocked ? (
        <div className="card p-3.5 flex items-center gap-3 no-print" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
          <Icon name="clock" size={16} color="#EA580C" />
          <div className="flex-1 text-[13px]">
            لا يوجد لديك شفت مفتوح — الصرف النقدي للمورد يتطلب شفتاً مفتوحاً ليخرج المال من درج حقيقي.
          </div>
          <button type="button" className="btn btn-primary" onClick={() => openShiftModal('open')}>فتح شفت</button>
        </div>
      ) : shift ? (
        <div className="card p-3 flex items-center gap-3 text-[12.5px] no-print" style={{ background: '#F0FDF4', borderColor: '#BBF7D0' }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#22C55E' }} />
          <div className="flex-1">
            كل تسديد نقدي يخرج من القاصة التي تختارها ويظهر في تقرير الشفت والقاصة والتدفق النقدي.
            كلفة البضاعة سُجِّلت وقت الشراء، فلا تُحتسب مصروفاً ثانياً عند السداد.
          </div>
        </div>
      ) : null}

      {migrateResult ? (
        <div className="card p-3 text-[12.5px] no-print" style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}>
          <b>نتيجة معالجة البيانات السابقة:</b> فُحصت {migrateResult.invoicesScanned} فاتورة،
          وحُدِّث {migrateResult.invoicesUpdated} سجلاً، وأُعيد حساب أرصدة {migrateResult.suppliersUpdated} مورداً.
          {migrateResult.flaggedCount > 0 ? (
            <>
              {' '}و<b>{migrateResult.flaggedCount}</b> فاتورة بيانات دفعها ناقصة عُلِّمت «بحاجة مراجعة» ولم تُفترض غير مدفوعة —
              {' '}
              <button type="button" className="underline font-bold" onClick={() => { setTab('reports'); }}>
                اعرض القائمة في تبويب التقارير
              </button>.
            </>
          ) : ' ولا توجد سجلات بحاجة مراجعة.'}
          {' '}لم تُنشأ أي حركة قاصة جديدة.
          <button type="button" className="btn btn-ghost ms-2" onClick={() => setMigrateResult(null)}>
            <Icon name="x" size={12} />
          </button>
        </div>
      ) : null}

      <div className="no-print"><StatCards stats={stats} /></div>

      <Card>
        <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] mb-3 no-print">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={clsx(
                'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold',
                tab === t.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'reports' ? (
          <SupplierReports suppliers={allSuppliers} />
        ) : tab === 'vouchers' ? (
          <>
            <div className="flex items-center gap-2 flex-wrap mb-3 no-print">
              <input
                className="input"
                style={{ width: 260 }}
                placeholder="بحث برقم السند أو الفاتورة أو المورد..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setSearch(q); }}
              />
              <button type="button" className="btn" onClick={() => setSearch(q)}><Icon name="search" size={13} /> بحث</button>
              <label className="text-[12px] text-ink-3">من</label>
              <input type="date" className="input" style={{ width: 145 }} value={from} onChange={(e) => setFrom(e.target.value)} />
              <label className="text-[12px] text-ink-3">إلى</label>
              <input type="date" className="input" style={{ width: 145 }} value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            {vouchersQ.isLoading ? (
              <div className="text-center py-10 text-ink-3">جاري التحميل...</div>
            ) : !vouchers.length ? (
              <div className="text-center py-10 text-ink-3">لا سندات صرف</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="text-ink-3 border-b border-line text-right">
                      <th className="p-2">رقم السند</th>
                      <th className="p-2">التاريخ</th>
                      <th className="p-2">المورد</th>
                      <th className="p-2">النوع</th>
                      <th className="p-2">المبلغ</th>
                      <th className="p-2">الطريقة</th>
                      <th className="p-2">القاصة</th>
                      <th className="p-2">المستخدم</th>
                      <th className="p-2">الفاتورة</th>
                      <th className="p-2">الحالة</th>
                      <th className="p-2">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vouchers.map((p) => (
                      <tr key={String(p._id)} className="border-b border-line">
                        <td className="p-2 font-mono">{p.voucherNumber}</td>
                        <td className="p-2 text-ink-3">{formatDateTime(p.paidAt)}</td>
                        <td className="p-2 font-bold">{p.supplierName}</td>
                        <td className="p-2">{p.direction === 'out' ? 'تسديد' : 'استرداد'}</td>
                        <td className={clsx('p-2 font-bold', p.status === 'reversed' && 'line-through text-ink-3')}>
                          {formatIQD(p.amount)}
                        </td>
                        <td className="p-2">{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</td>
                        <td className="p-2 text-ink-3">{p.cashboxName || '—'}</td>
                        <td className="p-2 text-ink-3">{p.userName || '—'}</td>
                        <td className="p-2 font-mono text-ink-3">
                          {p.invoiceNumber || (p.allocations || []).map((a) => a.invoiceNumber).join('، ') || '—'}
                        </td>
                        <td className="p-2">
                          {p.status === 'reversed'
                            ? <Pill variant="danger">معكوس</Pill>
                            : <Pill variant="success">مرحّل</Pill>}
                        </td>
                        <td className="p-2">
                          <div className="flex gap-1">
                            <button type="button" className="btn" style={{ padding: '4px 8px' }} title="طباعة السند" onClick={() => setVoucher(p)}>
                              <Icon name="print" size={13} />
                            </button>
                            {canReverse && p.status === 'posted' ? (
                              <button
                                type="button"
                                className="btn"
                                style={{ padding: '4px 8px', color: '#B91C1C' }}
                                title="قيد عكسي (السندات لا تُحذف)"
                                onClick={() => { setReverseTarget(p); setReverseReason(''); }}
                              >
                                <Icon name="x" size={13} />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[11.5px] text-ink-3 mt-2">
                  سندات الصرف لا تُحذف إطلاقاً — التصحيح يتم بقيد عكسي موثّق يبقى ظاهراً في كشف الحساب.
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap mb-3 no-print">
              <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] flex-wrap">
                {STATES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setFilter(() => setState(s.key))}
                    className={clsx(
                      'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold',
                      state === s.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <input
                className="input"
                style={{ width: 260 }}
                placeholder="بحث باسم المورد أو هاتفه..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setFilter(() => setSearch(q)); }}
              />
              <button type="button" className="btn" onClick={() => setFilter(() => setSearch(q))}>
                <Icon name="search" size={13} /> بحث
              </button>
              {search ? (
                <button type="button" className="btn btn-ghost" onClick={() => setFilter(() => { setQ(''); setSearch(''); })}>
                  <Icon name="x" size={13} /> مسح
                </button>
              ) : null}

              <label className="text-[12px] text-ink-3">من</label>
              <input type="date" className="input" style={{ width: 145 }} value={from} onChange={(e) => setFilter(() => setFrom(e.target.value))} />
              <label className="text-[12px] text-ink-3">إلى</label>
              <input type="date" className="input" style={{ width: 145 }} value={to} onChange={(e) => setFilter(() => setTo(e.target.value))} />

              <button
                type="button"
                className={clsx('btn', showAll && 'btn-primary')}
                onClick={() => { setShowAll((v) => !v); setPage(1); }}
                title="عرض كل الموردين بلا ترقيم — الفلاتر تبقى مطبّقة"
              >
                <Icon name="inventory" size={13} /> {showAll ? 'عرض الكل (مفعّل)' : 'عرض الكل'}
              </button>

              <div className="ms-auto flex items-center gap-2">
                <ExportButton
                  rows={exportRows}
                  columns={exportColumns}
                  filename="أرصدة-الموردين"
                  title="حسابات الموردين"
                  tableRef={tableRef}
                />
                <button type="button" className="btn" onClick={() => window.print()}>
                  <Icon name="print" size={13} /> طباعة
                </button>
                <button type="button" className="btn" onClick={() => listQ.refetch()}>
                  <Icon name="clock" size={13} /> تحديث
                </button>
              </div>
            </div>

            {notice ? (
              <div className="card p-2.5 mb-2 text-[12.5px] no-print" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
                ⚠️ {notice}
              </div>
            ) : null}

            <div className="print-root">
              {/* شعار المركز من الإعدادات — أعلى الورقة ووسطها */}
              <PrintLogo />
              <div className="overflow-x-auto" ref={tableRef}>
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="text-ink-3 border-b border-line text-right">
                      <th className="p-2">اسم المورد</th>
                      <th className="p-2">الهاتف</th>
                      <th className="p-2">إجمالي المشتريات</th>
                      <th className="p-2">إجمالي المدفوع</th>
                      <th className="p-2">إجمالي المتبقي</th>
                      <th className="p-2">فواتير مستحقة</th>
                      <th className="p-2">حالة الحساب</th>
                      <th className="p-2">آخر فاتورة</th>
                      <th className="p-2 no-print">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listQ.isLoading ? (
                      <tr><td colSpan={9} className="text-center py-10 text-ink-3">جاري التحميل...</td></tr>
                    ) : !rows.length ? (
                      <tr><td colSpan={9} className="text-center py-10 text-ink-3">
                        {search || from || to || state !== 'all' ? 'لا نتائج مطابقة للفلاتر' : 'لا موردين بعد'}
                      </td></tr>
                    ) : rows.map((r) => (
                      <tr key={String(r._id)} className="border-b border-line">
                        <td className="p-2 font-bold">
                          {r.name}
                          {r.needsReview > 0 ? (
                            <span className="ms-1" title={`${r.needsReview} فاتورة بحاجة مراجعة`}>
                              <Pill variant="warning">مراجعة {r.needsReview}</Pill>
                            </span>
                          ) : null}
                        </td>
                        <td className="p-2 font-mono text-ink-2">{r.phone || '—'}</td>
                        <td className="p-2">{formatIQD(r.purchasesTotal)}</td>
                        <td className="p-2 text-green font-semibold">{formatIQD(r.paidTotal)}</td>
                        <td className="p-2 font-extrabold" style={{ color: r.remainingTotal ? '#B91C1C' : '#15803D' }}>
                          {r.creditBalance > 0 ? `(${formatIQD(r.creditBalance)} دائن)` : formatIQD(r.remainingTotal)}
                        </td>
                        <td className="p-2">{r.openInvoices}</td>
                        <td className="p-2"><StateBadge state={r.accountState} label={r.accountStateLabel} /></td>
                        <td className="p-2 text-ink-3">{r.lastInvoiceAt ? formatDate(r.lastInvoiceAt) : '—'}</td>
                        <td className="p-2 no-print">
                          <div className="flex gap-1">
                            {canSettle && r.remainingTotal > 0 ? (
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setSettleTarget({ supplierId: String(r._id), mode: 'settle' })}
                              >
                                <Icon name="check" size={13} /> تسديد
                              </button>
                            ) : null}
                            {canSettle && r.creditBalance > 0 ? (
                              <button
                                type="button"
                                className="btn"
                                title="تحصيل الرصيد الدائن من المورد"
                                onClick={() => setSettleTarget({ supplierId: String(r._id), mode: 'refund' })}
                              >
                                استرداد
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn"
                              title="كشف الحساب"
                              onClick={() => setStatementFor({ id: String(r._id), name: r.name })}
                            >
                              <Icon name="reports" size={13} />
                            </button>
                            <button
                              type="button"
                              className="btn"
                              title="فواتير المورد وسنداته"
                              onClick={() => setDetailId(String(r._id))}
                            >
                              <Icon name="purchase" size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between mt-2 text-[12px] no-print">
              <div className="text-ink-3">
                {mode === 'all'
                  ? `يُعرض ${rows.length} من ${listQ.data?.total || 0} مورداً — بلا ترقيم`
                  : `صفحة ${listQ.data?.page || 1} من ${listQ.data?.pages || 1} • ${listQ.data?.total || 0} مورد`}
              </div>
              {mode === 'page' && (listQ.data?.pages || 1) > 1 ? (
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="btn"
                    style={{ padding: '6px 10px' }}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={(listQ.data?.page || 1) <= 1}
                  >
                    <Icon name="chevronR" size={13} />
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{ padding: '6px 10px' }}
                    onClick={() => setPage((p) => p + 1)}
                    disabled={(listQ.data?.page || 1) >= (listQ.data?.pages || 1)}
                  >
                    <Icon name="chevronL" size={13} />
                  </button>
                </div>
              ) : null}
            </div>
          </>
        )}
      </Card>

      {settleTarget ? (
        <SettleSupplierModal
          supplierId={settleTarget.supplierId}
          invoice={settleTarget.invoice}
          mode={settleTarget.mode}
          onClose={() => setSettleTarget(null)}
          onDone={(res) => { if (res && res.payment) setVoucher(res.payment); }}
        />
      ) : null}

      {detailId ? (
        <SupplierDetailModal
          supplierId={detailId}
          canSettle={canSettle}
          onClose={() => setDetailId(null)}
          onVoucher={(p) => setVoucher(p)}
          onSettleInvoice={(inv) => { setSettleTarget({ supplierId: detailId, invoice: inv, mode: 'settle' }); setDetailId(null); }}
          onSettleAccount={() => { setSettleTarget({ supplierId: detailId, mode: 'settle' }); setDetailId(null); }}
          onStatement={() => {
            const row = rows.find((r) => String(r._id) === detailId);
            setStatementFor({ id: detailId, name: row ? row.name : '' });
            setDetailId(null);
          }}
        />
      ) : null}

      {statementFor ? (
        <StatementModal
          supplierId={statementFor.id}
          supplierName={statementFor.name}
          onClose={() => setStatementFor(null)}
          onSettle={canSettle ? () => {
            setSettleTarget({ supplierId: statementFor.id, mode: 'settle' });
            setStatementFor(null);
          } : null}
        />
      ) : null}

      {voucher ? <VoucherPrint voucher={voucher} onClose={() => setVoucher(null)} /> : null}

      {reverseTarget ? (
        <Modal
          title="قيد عكسي لسند صرف"
          sub={`${reverseTarget.voucherNumber} — ${formatIQD(reverseTarget.amount)}`}
          width={460}
          onClose={() => setReverseTarget(null)}
          footer={(
            <>
              <button type="button" className="btn flex-1 justify-center" onClick={() => setReverseTarget(null)}>إلغاء</button>
              <button
                type="button"
                className="btn btn-danger flex-1 justify-center"
                onClick={doReverse}
                disabled={reverse.isPending || reverseReason.trim().length < 3}
              >
                {reverse.isPending ? 'جاري التنفيذ...' : 'تسجيل القيد العكسي'}
              </button>
            </>
          )}
        >
          <div className="card p-3 text-[12.5px]" style={{ background: '#FEF2F2', borderColor: '#FECACA' }}>
            السند لا يُحذف. سيُسجَّل قيد عكسي موثّق: يرجع المبلغ على ذمة المورد،
            {reverseTarget.paymentMethod === 'cash'
              ? ' ويعود النقد إلى قاصة درج شفتك بحركة حقيقية.'
              : ' ولا تتأثر أي قاصة (لم يخرج منها نقد أصلاً).'}
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">سبب العكس <span className="text-red">*</span></label>
            <input
              className="input"
              autoFocus
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              placeholder="مثال: سُجِّل على المورد الخطأ"
            />
          </div>
        </Modal>
      ) : null}

      <ConfirmModal
        open={migrateOpen}
        title="معالجة بيانات المشتريات السابقة"
        message={
          'سيُفحص كل فواتير المشتريات القديمة (آجل / واصل جزئي)، ويُشتق منها المدفوع والمتبقي، '
          + 'وتُربط بحسابات الموردين، وتُحسب الأرصدة الافتتاحية. لن تُنشأ أي حركة قاصة جديدة، '
          + 'والسجلات التي بيانات دفعها ناقصة ستُعلَّم «بحاجة مراجعة» بدل افتراضها غير مدفوعة. '
          + 'العملية آمنة للتكرار.'
        }
        confirmLabel="تشغيل المعالجة"
        loading={migrate.isPending}
        onClose={() => setMigrateOpen(false)}
        onConfirm={doMigrate}
      />
    </div>
  );
}
